import { useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { sendWelcomeEmail } from '../lib/emailService'

function OnboardingQuestionnaire({ user, onComplete }) {
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  // Company Information
  const [companyData, setCompanyData] = useState({
    // Step 1: Basic Company Info
    company_name: '',
    company_registration_number: '',
    tax_id: '',
    website: '',
    phone: '',
    address: '',
    city: '',
    country: 'Nigeria',
    
    // Step 2: Business Details
    business_type: 'real_estate_agency',
    years_in_business: '',
    number_of_branches: 1,
    number_of_agents: 1,
    primary_markets: [],
    services_offered: [],
    
    // Step 3: Subscription Plan
    selected_plan: 'professional',
    billing_cycle: 'monthly',
    
    // Step 4: Admin User Details
    first_name: '',
    last_name: '',
    position: 'CEO / Managing Director',
    
    // Terms
    agreed_to_terms: false
  })

  // ========== HELPER FUNCTIONS ==========
  const getPlanLimits = (planId) => {
    const plans = {
      free: { 
        max_agents: 1, 
        max_properties: 3, 
        max_leads: 10, 
        max_clients: 10,
        features: ['basic_support'] 
      },
      professional: { 
        max_agents: 5, 
        max_properties: 50, 
        max_leads: 500, 
        max_clients: 500,
        features: ['whatsapp', 'qr_codes', 'analytics', 'priority_support', 'email_marketing'] 
      },
      enterprise: { 
        max_agents: 999, 
        max_properties: 9999, 
        max_leads: 99999, 
        max_clients: 99999,
        features: ['whatsapp', 'qr_codes', 'analytics', 'priority_support', 'email_marketing', 'api_access', 'custom_features', 'dedicated_support'] 
      }
    }
    return plans[planId] || plans.professional
  }

  const allCountries = [
    'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco', 'Algeria', 'Tunisia', 'Libya', 'Sudan',
    'Ethiopia', 'Somalia', 'Uganda', 'Tanzania', 'Rwanda', 'Burundi', 'Djibouti', 'Eritrea', 'Zambia', 'Zimbabwe',
    'Botswana', 'Namibia', 'Mozambique', 'Malawi', 'Angola', 'Congo', 'DR Congo', 'Cameroon', 'Gabon', 'Equatorial Guinea',
    'Chad', 'Niger', 'Mali', 'Burkina Faso', 'Senegal', 'Gambia', 'Guinea', 'Guinea-Bissau', 'Sierra Leone', 'Liberia',
    'Ivory Coast', 'Benin', 'Togo', 'Mauritania', 'Cape Verde', 'Comoros', 'Mauritius', 'Seychelles', 'Lesotho', 'Eswatini',
    'Madagascar', 'Central African Republic', 'United States', 'Canada', 'Mexico', 'United Kingdom', 'Germany', 'France',
    'Italy', 'Spain', 'Portugal', 'Netherlands', 'Belgium', 'Austria', 'Switzerland', 'Sweden', 'Norway', 'Denmark',
    'Finland', 'Iceland', 'Ireland', 'Poland', 'Czech Republic', 'Slovakia', 'Hungary', 'Romania', 'Bulgaria', 'Greece',
    'Turkey', 'Cyprus', 'Malta', 'Croatia', 'Slovenia', 'Estonia', 'Latvia', 'Lithuania', 'Luxembourg', 'Monaco',
    'Andorra', 'Liechtenstein', 'San Marino', 'Vatican City', 'Montenegro', 'Serbia', 'Bosnia and Herzegovina', 'Albania',
    'North Macedonia', 'Kosovo', 'Belarus', 'Ukraine', 'Moldova', 'Russia', 'China', 'Japan', 'South Korea', 'North Korea',
    'Mongolia', 'Taiwan', 'India', 'Pakistan', 'Bangladesh', 'Nepal', 'Bhutan', 'Sri Lanka', 'Maldives', 'Indonesia',
    'Malaysia', 'Singapore', 'Philippines', 'Vietnam', 'Thailand', 'Myanmar', 'Laos', 'Cambodia', 'Brunei', 'East Timor',
    'Afghanistan', 'Uzbekistan', 'Kazakhstan', 'Kyrgyzstan', 'Tajikistan', 'Turkmenistan', 'Iran', 'Iraq', 'Saudi Arabia',
    'Yemen', 'Oman', 'UAE', 'Qatar', 'Bahrain', 'Kuwait', 'Jordan', 'Lebanon', 'Syria', 'Israel', 'Palestine', 'Armenia',
    'Azerbaijan', 'Georgia', 'Australia', 'New Zealand', 'Papua New Guinea', 'Fiji', 'Solomon Islands', 'Vanuatu', 'Samoa',
    'Tonga', 'Kiribati', 'Micronesia', 'Marshall Islands', 'Palau', 'Nauru', 'Tuvalu', 'Brazil', 'Argentina', 'Chile',
    'Peru', 'Colombia', 'Venezuela', 'Ecuador', 'Bolivia', 'Paraguay', 'Uruguay', 'Guyana', 'Suriname'
  ]

  const businessTypes = [
    { value: 'real_estate_agency', label: 'Real Estate Agency', icon: '🏢' },
    { value: 'property_management', label: 'Property Management Company', icon: '🏠' },
    { value: 'real_estate_developer', label: 'Real Estate Developer', icon: '🏗️' },
    { value: 'real_estate_consultant', label: 'Real Estate Consultant', icon: '📊' },
    { value: 'real_estate_investment', label: 'Investment Firm', icon: '💰' },
    { value: 'property_valuation', label: 'Property Valuation Firm', icon: '📈' },
    { value: 'other', label: 'Other', icon: '📌' }
  ]

  const markets = [
    'Residential Sales', 'Residential Rentals', 'Commercial Sales', 
    'Commercial Rentals', 'Luxury Properties', 'Off-Plan Properties', 
    'Property Management', 'Facility Management', 'Real Estate Consulting',
    'Property Valuation', 'Investment Advisory', 'International Properties'
  ]

  const services = [
    'Property Sales', 'Property Rentals', 'Property Management', 
    'Facility Management', 'Real Estate Consulting', 'Property Valuation',
    'Legal Documentation', 'Mortgage Advisory', 'Investment Analysis',
    'Market Research', 'Property Marketing', 'Lead Generation'
  ]

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price_monthly: 0,
      price_yearly: 0,
      features: ['1 Agent', '3 Properties', '10 Leads', 'Basic Support'],
      icon: '🆓'
    },
    {
      id: 'professional',
      name: 'Professional',
      price_monthly: 49,
      price_yearly: 499,
      features: ['5 Agents', '50 Properties', '500 Leads', 'Priority Support', 'WhatsApp Integration', 'QR Codes', 'Advanced Analytics'],
      icon: '⭐',
      recommended: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price_monthly: 199,
      price_yearly: 1990,
      features: ['Unlimited Agents', 'Unlimited Properties', 'Unlimited Leads', '24/7 Support', 'API Access', 'Custom Features', 'Dedicated Account Manager'],
      icon: '👑'
    }
  ]

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setCompanyData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleArrayChange = (name, value) => {
    setCompanyData(prev => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const planLimits = getPlanLimits(companyData.selected_plan)
      const subscription_status = companyData.selected_plan === 'free' ? 'active' : 'trial'
      const payment_status = companyData.selected_plan === 'free' ? 'completed' : 'pending'

      console.log('Step 1: Creating company...')

      // 1. Create company record
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .insert({
          name: companyData.company_name,
          registration_number: companyData.company_registration_number,
          tax_id: companyData.tax_id,
          website: companyData.website,
          phone: companyData.phone,
          address: companyData.address,
          city: companyData.city,
          country: companyData.country,
          business_type: companyData.business_type,
          years_in_business: parseInt(companyData.years_in_business) || 0,
          number_of_branches: parseInt(companyData.number_of_branches) || 1,
          primary_markets: companyData.primary_markets,
          services_offered: companyData.services_offered,
          subscription_plan: companyData.selected_plan,
          subscription_status: subscription_status,
          payment_status: payment_status,
          trial_ends_at: companyData.selected_plan !== 'free' ? new Date(Date.now() + 14 * 24 * 60 * 60 * 1000) : null,
          billing_cycle: companyData.billing_cycle,
          max_agents: planLimits.max_agents,
          max_properties: planLimits.max_properties,
          max_leads: planLimits.max_leads,
          max_clients: planLimits.max_clients,
          features_enabled: planLimits.features,
          onboarding_completed: true,
          created_by: user.id,
          created_at: new Date()
        })
        .select()
        .single()

      if (companyError) throw companyError
      
      console.log('Company created with ID:', company.id)

      // 2. Upsert user record with company_id and role
      console.log('Step 2: Upserting user with company_id...')

      const { error: upsertError } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          email: user.email,
          company_id: company.id,
          role: 'leadership',
          first_name: companyData.first_name,
          last_name: companyData.last_name,
          position: companyData.position,
          onboarding_completed: true,
          created_at: new Date(),
          updated_at: new Date()
        })

      if (upsertError) {
        console.error('Upsert error:', upsertError)
        throw upsertError
      }

      console.log('User upserted with company_id:', company.id)

      // 3. Create company settings
      await supabase
        .from('company_settings')
        .upsert({
          company_id: company.id,
          subscription_plan: companyData.selected_plan,
          billing_cycle: companyData.billing_cycle,
          max_agents: planLimits.max_agents,
          max_properties: planLimits.max_properties,
          max_leads: planLimits.max_leads,
          max_clients: planLimits.max_clients,
          features_enabled: planLimits.features
        })

      console.log('Step 3: Company settings created')

      // 4. Send welcome email
      try {
        const welcomeResult = await sendWelcomeEmail(
          user.email,
          companyData.first_name || companyData.first_name || 'Admin',
          companyData.company_name
        )
        
        if (welcomeResult.success) {
          console.log('Welcome email sent successfully to:', user.email)
        } else {
          console.error('Failed to send welcome email:', welcomeResult.error)
        }
      } catch (emailErr) {
        console.error('Email sending error:', emailErr)
        // Don't block registration if email fails
      }

      // 5. Complete onboarding
      if (onComplete) {
        onComplete('leadership')
      }

      alert('Company registration completed successfully! A welcome email has been sent.')
      window.location.href = '/leadership'

    } catch (err) {
      console.error('Onboarding error:', err)
      setError(err.message || 'Registration failed. Please try again.')
      setLoading(false)
    }
  }

  const nextStep = () => {
    setStep(step + 1)
    window.scrollTo(0, 0)
  }

  const prevStep = () => {
    setStep(step - 1)
    window.scrollTo(0, 0)
  }

  const renderStep1 = () => (
    <div style={stepContainer}>
      <h2 style={stepTitle}>🏢 Company Information</h2>
      <p style={stepSubtitle}>Tell us about your real estate business</p>

      <div style={formGroup}>
        <label style={label}>Company Name *</label>
        <input
          type="text"
          name="company_name"
          value={companyData.company_name}
          onChange={handleChange}
          placeholder="e.g., EstatesFlow Realty Ltd"
          required
          style={inputStyle}
        />
      </div>

      <div style={twoColumnGrid}>
        <div style={formGroup}>
          <label style={label}>Registration Number</label>
          <input
            type="text"
            name="company_registration_number"
            value={companyData.company_registration_number}
            onChange={handleChange}
            placeholder="RC Number / Business Reg"
            style={inputStyle}
          />
        </div>
        <div style={formGroup}>
          <label style={label}>Tax ID (TIN)</label>
          <input
            type="text"
            name="tax_id"
            value={companyData.tax_id}
            onChange={handleChange}
            placeholder="Tax Identification Number"
            style={inputStyle}
          />
        </div>
      </div>

      <div style={twoColumnGrid}>
        <div style={formGroup}>
          <label style={label}>Phone Number *</label>
          <input
            type="tel"
            name="phone"
            value={companyData.phone}
            onChange={handleChange}
            placeholder="+234 800 000 0000"
            required
            style={inputStyle}
          />
        </div>
        <div style={formGroup}>
          <label style={label}>Website</label>
          <input
            type="url"
            name="website"
            value={companyData.website}
            onChange={handleChange}
            placeholder="https://yourcompany.com"
            style={inputStyle}
          />
        </div>
      </div>

      <div style={formGroup}>
        <label style={label}>Business Address *</label>
        <input
          type="text"
          name="address"
          value={companyData.address}
          onChange={handleChange}
          placeholder="Street address"
          required
          style={inputStyle}
        />
      </div>

      <div style={twoColumnGrid}>
        <div style={formGroup}>
          <label style={label}>City *</label>
          <input
            type="text"
            name="city"
            value={companyData.city}
            onChange={handleChange}
            placeholder="Lagos, Abuja, etc."
            required
            style={inputStyle}
          />
        </div>
        <div style={formGroup}>
          <label style={label}>Country *</label>
          <select
            name="country"
            value={companyData.country}
            onChange={handleChange}
            style={inputStyle}
          >
            {allCountries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={buttonGroup}>
        <button type="button" onClick={nextStep} style={nextButton}>
          Continue → 
        </button>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div style={stepContainer}>
      <h2 style={stepTitle}>📊 Business Details</h2>
      <p style={stepSubtitle}>Help us understand your business better</p>

      <div style={formGroup}>
        <label style={label}>Business Type *</label>
        <div style={optionsGrid}>
          {businessTypes.map(type => (
            <div
              key={type.value}
              onClick={() => setCompanyData(prev => ({ ...prev, business_type: type.value }))}
              style={{
                ...optionCard,
                background: companyData.business_type === type.value ? '#1e3a5f' : '#fff',
                color: companyData.business_type === type.value ? '#fff' : '#1e293b',
                border: companyData.business_type === type.value ? 'none' : '1px solid #e2e8f0'
              }}
            >
              <div style={{ fontSize: 32 }}>{type.icon}</div>
              <div style={{ fontWeight: 500 }}>{type.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={twoColumnGrid}>
        <div style={formGroup}>
          <label style={label}>Years in Business</label>
          <input
            type="number"
            name="years_in_business"
            value={companyData.years_in_business}
            onChange={handleChange}
            placeholder="e.g., 5"
            min="0"
            style={inputStyle}
          />
        </div>
        <div style={formGroup}>
          <label style={label}>Number of Branches</label>
          <input
            type="number"
            name="number_of_branches"
            value={companyData.number_of_branches}
            onChange={handleChange}
            placeholder="1"
            min="1"
            style={inputStyle}
          />
        </div>
      </div>

      <div style={formGroup}>
        <label style={label}>Primary Markets (select all that apply)</label>
        <div style={checkboxGrid}>
          {markets.map(market => (
            <label key={market} style={checkboxLabel}>
              <input
                type="checkbox"
                checked={companyData.primary_markets.includes(market)}
                onChange={() => handleArrayChange('primary_markets', market)}
                style={{ marginRight: '8px' }}
              />
              {market}
            </label>
          ))}
        </div>
      </div>

      <div style={formGroup}>
        <label style={label}>Services Offered (select all that apply)</label>
        <div style={checkboxGrid}>
          {services.map(service => (
            <label key={service} style={checkboxLabel}>
              <input
                type="checkbox"
                checked={companyData.services_offered.includes(service)}
                onChange={() => handleArrayChange('services_offered', service)}
                style={{ marginRight: '8px' }}
              />
              {service}
            </label>
          ))}
        </div>
      </div>

      <div style={buttonGroup}>
        <button type="button" onClick={prevStep} style={backButton}>
          ← Back
        </button>
        <button type="button" onClick={nextStep} style={nextButton}>
          Continue →
        </button>
      </div>
    </div>
  )

  const renderStep3 = () => (
    <div style={stepContainer}>
      <h2 style={stepTitle}>💳 Choose Your Plan</h2>
      <p style={stepSubtitle}>Select the plan that best fits your business</p>

      <div style={pricingGrid}>
        {plans.map(plan => (
          <div
            key={plan.id}
            onClick={() => setCompanyData(prev => ({ ...prev, selected_plan: plan.id }))}
            style={{
              ...pricingCard,
              border: companyData.selected_plan === plan.id ? '2px solid #1e3a5f' : '1px solid #e2e8f0',
              transform: companyData.selected_plan === plan.id ? 'scale(1.02)' : 'scale(1)'
            }}
          >
            {plan.recommended && <div style={recommendedBadge}>Most Popular</div>}
            <div style={{ fontSize: 48, marginBottom: 16 }}>{plan.icon}</div>
            <h3 style={{ margin: 0, fontSize: 24 }}>{plan.name}</h3>
            
            <div style={{ margin: '16px 0' }}>
              <span style={{ fontSize: 36, fontWeight: 'bold' }}>${plan.price_monthly}</span>
              <span style={{ color: '#64748b' }}>/month</span>
            </div>
            
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
              or ${plan.price_yearly}/year
            </div>
            
            <ul style={{ textAlign: 'left', margin: '16px 0', padding: 0, listStyle: 'none' }}>
              {plan.features.map((feature, idx) => (
                <li key={idx} style={{ padding: '4px 0', fontSize: 14 }}>✓ {feature}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div style={formGroup}>
        <label style={label}>Billing Cycle</label>
        <div style={billingToggle}>
          <button
            type="button"
            onClick={() => setCompanyData(prev => ({ ...prev, billing_cycle: 'monthly' }))}
            style={{
              ...billingButton,
              background: companyData.billing_cycle === 'monthly' ? '#1e3a5f' : '#f8fafc',
              color: companyData.billing_cycle === 'monthly' ? '#fff' : '#1e293b'
            }}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setCompanyData(prev => ({ ...prev, billing_cycle: 'yearly' }))}
            style={{
              ...billingButton,
              background: companyData.billing_cycle === 'yearly' ? '#1e3a5f' : '#f8fafc',
              color: companyData.billing_cycle === 'yearly' ? '#fff' : '#1e293b'
            }}
          >
            Yearly (Save 15%)
          </button>
        </div>
      </div>

      <div style={buttonGroup}>
        <button type="button" onClick={prevStep} style={backButton}>
          ← Back
        </button>
        <button type="button" onClick={nextStep} style={nextButton}>
          Continue →
        </button>
      </div>
    </div>
  )

  const renderStep4 = () => (
    <div style={stepContainer}>
      <h2 style={stepTitle}>👤 Admin Account</h2>
      <p style={stepSubtitle}>Your account details as company administrator</p>

      <div style={twoColumnGrid}>
        <div style={formGroup}>
          <label style={label}>First Name *</label>
          <input
            type="text"
            name="first_name"
            value={companyData.first_name}
            onChange={handleChange}
            required
            style={inputStyle}
          />
        </div>
        <div style={formGroup}>
          <label style={label}>Last Name *</label>
          <input
            type="text"
            name="last_name"
            value={companyData.last_name}
            onChange={handleChange}
            required
            style={inputStyle}
          />
        </div>
      </div>

      <div style={formGroup}>
        <label style={label}>Email (Company Account)</label>
        <input
          type="email"
          value={user.email}
          disabled
          style={{ ...inputStyle, background: '#f1f5f9' }}
        />
      </div>

      <div style={formGroup}>
        <label style={label}>Position / Title *</label>
        <select
          name="position"
          value={companyData.position}
          onChange={handleChange}
          style={inputStyle}
        >
          <option value="CEO / Managing Director">CEO / Managing Director</option>
          <option value="Owner / Founder">Owner / Founder</option>
          <option value="Branch Manager">Branch Manager</option>
          <option value="Operations Director">Operations Director</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div style={formGroup}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            name="agreed_to_terms"
            checked={companyData.agreed_to_terms}
            onChange={handleChange}
            required
          />
          I agree to the <a href="/terms" target="_blank" style={{ color: '#1e3a5f' }}>Terms of Service</a> and 
          <a href="/privacy" target="_blank" style={{ color: '#1e3a5f' }}> Privacy Policy</a>
        </label>
      </div>

      <div style={infoBox}>
        <div style={{ fontSize: 20, marginBottom: 8 }}>🎉 14-Day Free Trial</div>
        <p style={{ margin: 0, fontSize: 14 }}>
          Your subscription includes a 14-day free trial. No charges until after the trial period.
        </p>
      </div>

      <div style={buttonGroup}>
        <button type="button" onClick={prevStep} style={backButton}>
          ← Back
        </button>
        <button type="submit" disabled={loading} style={submitButton}>
          {loading ? 'Setting up your company...' : 'Complete Registration →'}
        </button>
      </div>
    </div>
  )

  return (
    <div style={containerStyle}>
      <div style={onboardingCard}>
        {/* Progress Bar */}
        <div style={progressContainer}>
          <div style={progressSteps}>
            {[1, 2, 3, 4].map(stepNum => (
              <div key={stepNum} style={{ textAlign: 'center', flex: 1 }}>
                <div
                  style={{
                    ...stepCircle,
                    background: step >= stepNum ? '#1e3a5f' : '#e2e8f0',
                    color: step >= stepNum ? '#fff' : '#94a3b8'
                  }}
                >
                  {stepNum}
                </div>
                <div style={stepLabel}>
                  {stepNum === 1 && 'Company'}
                  {stepNum === 2 && 'Business'}
                  {stepNum === 3 && 'Plan'}
                  {stepNum === 4 && 'Complete'}
                </div>
              </div>
            ))}
          </div>
          <div style={progressBar}>
            <div style={{ ...progressFill, width: `${(step - 1) * 33.33}%` }} />
          </div>
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </form>
      </div>
    </div>
  )
}

// ========== STYLES ==========
const containerStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
  padding: '40px 20px',
  fontFamily: "'Inter', system-ui, sans-serif"
}

const onboardingCard = {
  maxWidth: '800px',
  margin: '0 auto',
  background: '#fff',
  borderRadius: '24px',
  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  padding: '40px'
}

const progressContainer = {
  marginBottom: '40px'
}

const progressSteps = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '16px',
  position: 'relative',
  zIndex: 1
}

const stepCircle = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  margin: '0 auto',
  transition: 'all 0.3s'
}

const stepLabel = {
  fontSize: '12px',
  marginTop: '8px',
  color: '#64748b'
}

const progressBar = {
  height: '4px',
  background: '#e2e8f0',
  borderRadius: '2px',
  marginTop: '-20px'
}

const progressFill = {
  height: '100%',
  background: '#1e3a5f',
  borderRadius: '2px',
  transition: 'width 0.3s'
}

const stepContainer = {
  animation: 'fadeIn 0.3s ease'
}

const stepTitle = {
  fontSize: '28px',
  marginBottom: '8px',
  color: '#1e3a5f'
}

const stepSubtitle = {
  color: '#64748b',
  marginBottom: '32px'
}

const formGroup = {
  marginBottom: '24px'
}

const label = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '500',
  color: '#1e293b'
}

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  fontSize: '16px',
  fontFamily: 'inherit',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box'
}

const twoColumnGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px'
}

const optionsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
  gap: '12px'
}

const optionCard = {
  padding: '16px',
  borderRadius: '12px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s'
}

const checkboxGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: '12px',
  maxHeight: '200px',
  overflowY: 'auto',
  padding: '8px',
  border: '1px solid #e2e8f0',
  borderRadius: '12px'
}

const checkboxLabel = {
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px',
  cursor: 'pointer'
}

const pricingGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '20px',
  marginBottom: '32px'
}

const pricingCard = {
  padding: '24px',
  borderRadius: '16px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s',
  position: 'relative',
  background: '#fff'
}

const recommendedBadge = {
  position: 'absolute',
  top: '-12px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: '#f59e0b',
  color: '#fff',
  padding: '4px 12px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 'bold'
}

const billingToggle = {
  display: 'flex',
  gap: '12px'
}

const billingButton = {
  flex: 1,
  padding: '12px',
  borderRadius: '12px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s'
}

const buttonGroup = {
  display: 'flex',
  gap: '16px',
  justifyContent: 'space-between',
  marginTop: '32px'
}

const backButton = {
  padding: '12px 24px',
  background: '#f1f5f9',
  color: '#1e293b',
  border: 'none',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background 0.2s'
}

const nextButton = {
  padding: '12px 32px',
  background: '#1e3a5f',
  color: '#fff',
  border: 'none',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background 0.2s'
}

const submitButton = {
  padding: '14px 32px',
  background: '#059669',
  color: '#fff',
  border: 'none',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: '500',
  cursor: 'pointer',
  width: '100%',
  transition: 'background 0.2s'
}

const infoBox = {
  background: '#f0fdf4',
  padding: '20px',
  borderRadius: '12px',
  textAlign: 'center',
  marginTop: '24px'
}

const errorStyle = {
  background: '#fee2e2',
  color: '#dc2626',
  padding: '12px',
  borderRadius: '8px',
  marginBottom: '20px'
}

export default OnboardingQuestionnaire